/**
 * React Starter Kit for Firebase
 * https://github.com/kriasoft/react-firebase-starter
 * Copyright (c) 2015-present Kriasoft | MIT License
 */

import {
  createTheme,
  responsiveFontSizes,
  lighten,
} from "@mui/material/styles";
import { grey } from "@mui/material/colors";

// https://material-ui.com/customization/themes/
// https://material-ui.com/style/color/
let theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#FF7F50",
      // https://stackoverflow.com/a/55534820/9793171
      contrastText: "#ffffff",
    },
    secondary: {
      main: grey[900],
    },
    heading: {
      main: "#323648",
      light: "#5b5e6D",
    },
    linkColor: {
      main: "#1976D2",
    },
    LPBlue: {
      light: lighten("#3098F3", 0.88),
      main: "#3098F3",
      contrastText: "#ffffff",
    },
  },

  typography: {
    fontFamily: ["Averta Standard", "Helvetica", "Arial", "sans-serif"].join(
      ","
    ),

    h1: {
      fontSize: "5rem",
      fontWeight: "600",
      lineHeight: "1.15",
      letterSpacing: "-0.025em",
    },
    h2: {
      fontSize: "4rem",
      fontWeight: "600",
      lineHeight: "1.1875",
      letterSpacing: "-0.025em",
    },
    h3: {
      fontSize: "3rem",
      fontWeight: "600",
      lineHeight: "1.25",
      letterSpacing: "-0.025em",
    },
    h4: {
      fontSize: "2rem",
      fontWeight: "600",
      lineHeight: "1.375",
      letterSpacing: "-0.025em",
    },
    h5: {
      fontSize: "1.5rem",
      fontWeight: "600",
      lineHeight: 1.5,
      letterSpacing: "-0.025em",
    },
    h6: {
      fontSize: "1.25rem",
      fontWeight: "600",
      lineHeight: "1.4",
      letterSpacing: "-0.025em",
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: "600",
      lineHeight: "1.375",
      letterSpacing: "-0.025em",
    },
    subtitle2: {
      fontSize: ".875rem",
      fontWeight: "600",
      lineHeight: "1.429",
      letterSpacing: "-0.025em",
    },
    body1: {
      fontSize: "1rem",
      fontWeight: "400",
      lineHeight: "1.5",
      letterSpacing: "normal",
    },
    body2: {
      fontSize: ".875rem",
      fontWeight: "400",
      lineHeight: "1.429",
      letterSpacing: "normal",
    },
    button: {
      textTransform: "none",
      fontSize: ".875rem",
      fontWeight: "600",
      lineHeight: "1.75",
      //letterSpacing: '0.02857em',
    },
    caption: {
      fontSize: ".75rem",
      fontWeight: "400",
      lineHeight: "1.5",
      letterSpacing: "0.025em",
    },
    overline: {
      fontSize: ".75rem",
      fontWeight: "700",
      lineHeight: "1.5",
      //letterSpacing: '0.08333em',
      textTransform: "uppercase",
    },
  },

  contentWidth: {
    small: 704,
    medium: 1008,
    wide: 1216,
  },

  breakpoints: {
    values: {
      xs: 0,
      se: 360, // iphone SE
      mob: 480, // custom mobile breakpoint; used on dashboard
      sm: 600,
      tab: 768, // tablet
      md: 960,
      ccb: 1024, // crisp-chat-breakpoint
      lg: 1280,
      xl: 1920,
    },
  },

  components: {
    MuiButton: {
      defaultProps: {
        color: "secondary",
        disableElevation: true,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        color: "secondary",
      },
      styleOverrides: {
        grouped: {
          border: "1px solid rgba(0,0,0,0.23)",
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiTextField: {
      defaultProps: {
        color: "secondary",
        variant: "standard",
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiInputLabel: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "rgba(0, 0, 0, 0.08) !important",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "rgba(0, 0, 0, 0.54)",
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: "#fff",
            backgroundColor: grey[600],
            "&:hover": {
              color: "#fff",
              backgroundColor: grey[600],
            },
          },
        },
        label: {
          position: "absolute",
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          "&:not(:first-of-type)": {
            margin: 2,
            border: "1px solid transparent",
            borderRadius: "50%",
          },
          "&:not(:last-of-type)": {
            margin: 2,
            border: "1px solid transparent",
            borderRadius: "50%",
          },
        },
      },
    },
  },
});

theme.mixins.link = {
  color: theme.palette.primary.main,
  cursor: "pointer",
  textDecoration: "none",
  "&:hover,&:active": {
    textDecoration: "underline",
  },
};

theme.components.MuiAlert = {
  // Override the styling for alert background color
  styleOverrides: {
    standardInfo: {
      color: theme.palette.text.primary,
      backgroundColor: lighten(theme.palette.info.light, 0.85),
    },
    standardError: {
      color: theme.palette.text.primary,
      backgroundColor: lighten(theme.palette.error.light, 0.85),
    },
    standardSuccess: {
      color: theme.palette.text.primary,
      backgroundColor: lighten(theme.palette.success.light, 0.85),
    },
    standardWarning: {
      color: theme.palette.text.primary,
      backgroundColor: lighten(theme.palette.warning.light, 0.85),
    },
  },
};

theme = responsiveFontSizes(theme, { breakpoints: ["sm"] });

export default theme;
